import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle, CalendarPlus, CheckCheck, Smile, Wallet, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { KpiCard, PageHeader, Empty } from "@/components/KpiCard";
import { ChargeBadge, ReturnBadge } from "@/components/StatusBadge";
import { PaymentDialog } from "@/components/PaymentDialog";
import { WhatsAppDialog } from "@/components/WhatsAppDialog";
import { AppointmentDialog } from "@/components/AppointmentDialog";
import { useApi, useResource, errMsg, notifyRefresh } from "@/lib/api";
import { brl, fmtDate, monthLabel, todayISO } from "@/lib/constants";

const Ortho = ({ onWa }) => {
  const [month, setMonth] = useState(todayISO().slice(0, 7));
  const [pay, setPay] = useState(null);
  const { data } = useResource(`/mensalidades?month=${month}`);
  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <Input type="month" value={month} max={todayISO().slice(0, 7)} onChange={(e) => e.target.value && setMonth(e.target.value)} className="w-48 bg-white" data-testid="ortho-month-input" />
        <span className="text-sm capitalize text-slate-500">{monthLabel(month)}</span>
      </div>
      <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard label="Pacientes com aparelho" value={data?.ortho_patients ?? "—"} icon={Smile} tone="cyan" testId="ortho-kpi-patients" />
        <KpiCard label="Previsto no mês" value={brl(data?.expected)} icon={Wallet} testId="ortho-kpi-expected" />
        <KpiCard label="Recebido" value={brl(data?.received)} tone="emerald" testId="ortho-kpi-received" />
        <KpiCard label="Pendente" value={brl(data?.pending)} icon={Clock} tone="rose" testId="ortho-kpi-pending" />
      </div>
      <div className="surface overflow-x-auto">
        {data && data.items.length === 0 ? <div className="p-6"><Empty>Nenhuma mensalidade neste mês</Empty></div> : (
          <table className="w-full min-w-[640px] text-sm" data-testid="ortho-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr><th className="px-5 py-3">Paciente</th><th className="px-3">Vencimento</th><th className="px-3 text-right">Valor</th><th className="px-3">Status</th><th className="px-3"></th></tr>
            </thead>
            <tbody>
              {(data?.items || []).map((c) => (
                <tr key={c.id} className="border-t border-slate-100" data-testid={`ortho-row-${c.id}`}>
                  <td className="px-5 py-3"><Link to={`/pacientes/${c.patient_id}`} className="font-semibold text-slate-800 hover:text-sky-700">{c.patient_name}</Link></td>
                  <td className="font-mono-num px-3 text-slate-500">{fmtDate(c.due_date)}</td>
                  <td className="font-mono-num px-3 text-right font-semibold">{brl(c.amount)}</td>
                  <td className="px-3"><ChargeBadge status={c.status} testId={`ortho-status-${c.id}`} /></td>
                  <td className="whitespace-nowrap px-3 text-right">
                    {c.status !== "pago" && (
                      <>
                        <Button size="sm" variant="ghost" className="text-emerald-700 hover:bg-emerald-50" onClick={() => onWa({ name: c.patient_name, phone: c.patient_phone, template: "mensalidade", vars: { valor: brl(c.remaining), vencimento: fmtDate(c.due_date) } })} data-testid={`ortho-wa-${c.id}`}><MessageCircle className="h-4 w-4" /></Button>
                        <Button size="sm" className="rounded-full bg-sky-600 hover:bg-sky-700" onClick={() => setPay(c)} data-testid={`ortho-pay-${c.id}`}>Dar baixa</Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <PaymentDialog open={!!pay} onOpenChange={(v) => !v && setPay(null)} patientId={pay?.patient_id} charge={pay} />
    </div>
  );
};

const Returns = ({ onWa }) => {
  const api = useApi();
  const { data } = useResource("/retornos");
  const [appt, setAppt] = useState(null);
  const done = async (r) => {
    try { const res = await api.post(`/patients/${r.id}/return-done`); toast.success(`Próximo retorno: ${fmtDate(res.data.next_date)}`); notifyRefresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="surface overflow-x-auto">
      {data && data.length === 0 ? <div className="p-6"><Empty>Nenhum paciente com plano de retorno. Ative em “Editar paciente”.</Empty></div> : (
        <table className="w-full min-w-[640px] text-sm" data-testid="returns-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr><th className="px-5 py-3">Paciente</th><th className="px-3">Intervalo</th><th className="px-3">Próximo retorno</th><th className="px-3">Situação</th><th className="px-3"></th></tr>
          </thead>
          <tbody>
            {(data || []).map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`return-row-${r.id}`}>
                <td className="px-5 py-3"><Link to={`/pacientes/${r.id}`} className="font-semibold text-slate-800 hover:text-sky-700">{r.name}</Link></td>
                <td className="px-3 text-slate-500">{r.return_plan.interval_months} meses</td>
                <td className="font-mono-num px-3 text-slate-600">{fmtDate(r.return_plan.next_date)}</td>
                <td className="px-3"><ReturnBadge status={r.status} testId={`return-status-${r.id}`} /></td>
                <td className="whitespace-nowrap px-3 text-right">
                  <Button size="sm" variant="ghost" className="text-emerald-700 hover:bg-emerald-50" onClick={() => onWa({ name: r.name, phone: r.phone, template: "retorno", vars: {} })} data-testid={`return-wa-${r.id}`}><MessageCircle className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-sky-700 hover:bg-sky-50" onClick={() => setAppt({ patient_id: r.id, procedure: "Retorno" })} data-testid={`return-schedule-${r.id}`}><CalendarPlus className="mr-1 h-4 w-4" /> Agendar</Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => done(r)} data-testid={`return-done-${r.id}`}><CheckCheck className="mr-1 h-4 w-4" /> Realizado</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <AppointmentDialog open={!!appt} onOpenChange={(v) => !v && setAppt(null)} initial={appt} />
    </div>
  );
};

export default function Mensalidades() {
  const [params, setParams] = useSearchParams();
  const [wa, setWa] = useState(null);
  const tab = params.get("tab") || "aparelho";
  return (
    <div>
      <PageHeader title="Mensalidades & Retornos" subtitle="Pacientes em tratamento com aparelho e consultas periódicas de retorno" />
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList className="mb-5 bg-sky-50">
          <TabsTrigger value="aparelho" data-testid="tab-aparelho">Aparelho ortodôntico</TabsTrigger>
          <TabsTrigger value="retornos" data-testid="tab-retornos">Retornos</TabsTrigger>
        </TabsList>
        <TabsContent value="aparelho"><Ortho onWa={setWa} /></TabsContent>
        <TabsContent value="retornos"><Returns onWa={setWa} /></TabsContent>
      </Tabs>
      <WhatsAppDialog open={!!wa} onOpenChange={(v) => !v && setWa(null)} name={wa?.name} phone={wa?.phone} template={wa?.template} vars={wa?.vars || {}} />
    </div>
  );
}
